import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { providersAPI, marketsAPI, jobsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import ProviderCard from '../components/ProviderCard';
import MarketTicker from '../components/MarketTicker';
import Modal from '../components/Modal';

const SERVICES = ['Data Center Hands & Feet','Rack & Stack','Cabling & Patching','Hardware Deployment','Remote Hands','Structured Cabling','Network Setup'];

export default function BrowsePage() {
  const [providers, setProviders]   = useState([]);
  const [markets,   setMarkets]     = useState([]);
  const [rates,     setRates]       = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [selected,  setSelected]    = useState(null);
  const [hiring,    setHiring]      = useState(false);
  const [hired,     setHired]       = useState(null);
  const [error,     setError]       = useState('');
  const [search,    setSearch]      = useState('');
  const [params, setParams]         = useSearchParams();
  const marketId = params.get('market') || '';
  const { user, role } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({ description:'', start_date:'', hours_estimated:'8', location_address:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes, rRes] = await Promise.all([
        providersAPI.browse({ market_id: marketId||undefined, search: search||undefined }),
        marketsAPI.list(),
        marketsAPI.rates(),
      ]);
      setProviders(pRes.data);
      setMarkets(mRes.data);
      setRates(rRes.data);
    } catch(e) { setError('Failed to load providers'); }
    setLoading(false);
  }, [marketId, search]);

  useEffect(() => { load(); }, [load]);

  const marketRate = rates.find(r=>r.market_id==marketId)?.rate_avg || 90;

  const handleHire = async () => {
    if (!user) { nav('/register'); return; }
    if (role !== 'customer') { setError('Only customers can hire providers'); return; }
    setHiring(true); setError('');
    try {
      const res = await jobsAPI.create({ provider_id: selected.id, ...form, service_id: 1 });
      setHired(res.data.job);
    } catch(e) { setError(e.response?.data?.error || 'Failed to create job'); }
    setHiring(false);
  };

  const total        = selected ? (parseFloat(form.hours_estimated||8) * selected.rate_hr) * 1.1 : 0;
  const platformFee  = total - (total / 1.1);

  return (
    <div className="page-container">
      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <div className="section-title" style={{flex:'0 0 auto'}}>Browse Professionals</div>
        <div style={{flex:1}}/>
        <input style={{maxWidth:220}} placeholder="Search skills..." value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={{width:'auto'}} value={marketId} onChange={e=>setParams(e.target.value?{market:e.target.value}:{})}>
          <option value="">All Markets</option>
          {markets.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <MarketTicker selectedMarketId={marketId?parseInt(marketId):null} />

      {/* Priority legend */}
      <div style={{display:'flex',gap:16,alignItems:'center',padding:'10px 14px',background:'rgba(255,255,255,0.02)',
        border:'1px solid rgba(255,255,255,0.05)',borderRadius:8,marginBottom:18,flexWrap:'wrap',fontSize:12,color:'#8899AA'}}>
        <span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,fontWeight:600,color:'#445566'}}>Priority:</span>
        {[['#6DBE45','Elite (Multi-cert)'],['#1E7FD4','Certified'],['#445566','Verified (No Cert)']].map(([c,l])=>(
          <span key={l} style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:9,height:9,borderRadius:2,background:c,flexShrink:0}}/>  {l}
          </span>
        ))}
        <span style={{marginLeft:'auto'}}>{providers.length} providers</span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{textAlign:'center',padding:'60px 0'}}><span className="spinner"/></div>
      ) : providers.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px 0',color:'#445566'}}>No providers found. Try adjusting filters.</div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
          {providers.map(p=>(
            <ProviderCard key={p.id} provider={p} marketRate={parseFloat(marketRate)} onSelect={setSelected}/>
          ))}
        </div>
      )}

      {/* Hire modal */}
      {selected && !hired && (
        <Modal title="Select Provider" sub="Review before revealing identity" onClose={()=>{setSelected(null);setError('');}}>
          <div style={{background:'rgba(0,194,255,0.06)',border:'1px solid rgba(0,194,255,0.2)',borderRadius:8,padding:16,marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{selected.code_id} — {selected.specialty}</div>
                <div style={{fontSize:12,color:'#8899AA',marginTop:2}}>{selected.experience_years} years · Rating {selected.avg_rating>0?selected.avg_rating:'New'}</div>
                <div style={{display:'flex',gap:5,marginTop:6,flexWrap:'wrap'}}>
                  {(selected.certifications||[]).map(c=><span key={c} className="badge badge-cert">{c}</span>)}
                  {selected.insured&&<span className="badge badge-insured">Insured</span>}
                </div>
              </div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:800,color:'#1E7FD4',flexShrink:0}}>${selected.rate_hr}/hr</div>
            </div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {user ? <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" value={form.start_date} onChange={e=>setForm(f=>({...f,start_date:e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Estimated Hours</label>
                <input type="number" min="1" value={form.hours_estimated} onChange={e=>setForm(f=>({...f,hours_estimated:e.target.value}))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Location / Data Center Address</label>
              <input placeholder="Address or DC name" value={form.location_address} onChange={e=>setForm(f=>({...f,location_address:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Work Description</label>
              <textarea rows={3} style={{resize:'vertical'}} placeholder="Describe the work needed..." value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
            </div>

            <div style={{background:'#0B1221',border:'1px solid rgba(255,255,255,0.07)',borderRadius:8,padding:14,marginBottom:16}}>
              {[
                ['Provider Rate (est. '+form.hours_estimated+'hrs)', `$${(selected.rate_hr*parseFloat(form.hours_estimated||8)).toFixed(2)}`],
                ['iTekPros Platform Fee (10%)', `$${platformFee.toFixed(2)}`],
              ].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)',fontSize:13,color:'#8899AA'}}>
                  <span>{l}</span><span style={{color:'#F0F6FF',fontWeight:500}}>{v}</span>
                </div>
              ))}
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0 0',fontSize:15,fontWeight:700,color:'#F0F6FF'}}>
                <span>Total Charged Now</span><span style={{color:'#1E7FD4'}}>${total.toFixed(2)}</span>
              </div>
            </div>
            <div style={{fontSize:11,color:'#445566',marginBottom:16}}>Payment held in escrow. Released after job completion. Final amount adjusts to actual hours.</div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>{setSelected(null);setError('');}}>Cancel</button>
              <button className={`btn ${selected.tier==='elite'?'btn-gold':'btn-primary'}`} onClick={handleHire} disabled={hiring}>
                {hiring?<><span className="spinner"/>  Processing...</>:`Confirm & Pay $${total.toFixed(2)}`}
              </button>
            </div>
          </> : (
            <>
              <div className="alert alert-info">Sign in or create a customer account to hire this provider.</div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={()=>setSelected(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={()=>nav('/register')}>Create Account →</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Success / reveal modal */}
      {hired && (
        <Modal title="Provider Confirmed!" onClose={()=>{setHired(null);setSelected(null);}}>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:48,marginBottom:8}}>✅</div>
            <div style={{fontSize:13,color:'#8899AA'}}>Identity revealed and connection initiated</div>
          </div>
          <div style={{background:'linear-gradient(135deg,rgba(0,194,255,0.05),rgba(0,102,255,0.05))',
            border:'1px solid rgba(0,194,255,0.3)',borderRadius:10,padding:20,textAlign:'center',marginBottom:16}}>
            <div style={{fontSize:11,color:'#8899AA',marginBottom:4,textTransform:'uppercase',letterSpacing:1}}>Your Provider</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:800,color:'#1E7FD4'}}>{selected?.real_name || 'Confirmed'}</div>
            <div style={{fontSize:13,color:'#AAB8C8',marginTop:4}}>Confirmation and contact details sent to your email</div>
          </div>
          <div style={{fontSize:12,color:'#556677',textAlign:'center',marginBottom:20}}>
            Job #{hired.id?.slice(0,8).toUpperCase()} · ${parseFloat(hired.total_charged||0).toFixed(2)} held in escrow
          </div>
          <button className="btn btn-primary" style={{width:'100%'}} onClick={()=>{setHired(null);setSelected(null);nav('/dashboard');}}>
            View My Jobs →
          </button>
        </Modal>
      )}
    </div>
  );
}
